import { useCallback, useMemo } from 'react'

import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidOfferPriceActions } from '@/features/bidding/types/bidOfferPriceFlow'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBid } from '@/features/bidding/utils/model/buildAggregatedBid'
import { aggregatedBidTransformer } from '@/features/bidding/utils/price/bidSaveTransformers'
import { validateAggregatedFlow } from '@/features/bidding/utils/validation/bidPriceFlowValidation'

import { useBidOfferPriceSaveActions } from './useBidOfferPriceSaveActions'

type UseBidOfferPriceActionsAggregatedParams = {
  groupParams: GroupedBidParams
}

/**
 * Hook that provides validation and save actions for the aggregated price flow.
 *
 * Implements the unified BidOfferPriceActions interface with aggregated-flow-specific logic:
 * - Validation: Checks for overbidding (negative remaining volume)
 * - Save: Transforms aggregated bid back to individual activation group bids and saves
 *
 * @param params - Configuration object containing groupParams
 * @returns Actions interface with validate, save functions and isSaving state
 */
export function useBidOfferPriceActions_Aggregated(
  params: UseBidOfferPriceActionsAggregatedParams,
): BidOfferPriceActions<{ aggregatedBid: AggregatedBid; draftBids: ExtendedBid[] }> {
  const { groupParams } = params
  const { saveBidsAndNavigate, isSaving } = useBidOfferPriceSaveActions({ groupParams })

  const validate = useCallback((bidData: { aggregatedBid: AggregatedBid; draftBids: ExtendedBid[] }) => {
    return validateAggregatedFlow(bidData.aggregatedBid)
  }, [])

  const save = useCallback(
    async (bidData: { aggregatedBid: AggregatedBid; draftBids: ExtendedBid[] }) => {
      const extendedBids = await aggregatedBidTransformer(bidData)
      await saveBidsAndNavigate(extendedBids)
    },
    [saveBidsAndNavigate],
  )

  return useMemo(
    () => ({
      validate,
      save,
      isSaving,
    }),
    [validate, save, isSaving],
  )
}
